import { type GetServerSideProps } from 'next';

import { AUTH_TOKEN, AUTH_USER } from '../../modules/user';

import { withEnhancedPageHandler } from '../../middlewares/withEnhancedPageHandler';

import { SignInProvider } from '../../context/SignInContext/SignInContext';

import { localizedContentService } from '../../services';

import { removeServerCookie } from '../../utils/api/cookies';

import { SignInTemplate } from '../../templates';

type TSimpleText = {
  text: string;
};

type TProfessionalLink = {
  text: string;
  href: string;
};

type TUnregistered = {
  text: string;
};

type TImageSize = {
  width: number;
  height: number;
};

type TImage = {
  src: string;
  size: TImageSize;
};

type TProfessional = {
  preText: string;
  link: TProfessionalLink;
  postText: string;
};

type TSendSmsButton = {
  sendPin: TSimpleText;
  unregistered: TUnregistered;
  professional: TProfessional;
};

type TPhoneScreenInput = {
  label: string;
  placeholder: string;
};

type TSendSms = {
  buttons: TSendSmsButton;
};

type TCountdownText = {
  prefix: string;
  suffix: string;
};

type TCountdown = {
  timeInSeconds: number;
  text: TCountdownText;
};

type TResendSms = {
  button: TSimpleText;
  countdown: TCountdown;
};

export type TUnregisteredDialog = {
  title: string;
  subtitle: string;
  image: TImage;
  button: {
    text: string;
    link: string;
  };
};

export type TPhoneScreen = {
  title: string;
  subtitle: string;
  image: TImage;
  input: TPhoneScreenInput;
  sendSms: TSendSms;
  unregisteredDialog: TUnregisteredDialog;
};

export type TPinScreen = {
  title: string;
  subtitle: string;
  resendSms: TResendSms;
};

export interface ISignInProps {
  signIn: {
    phoneScreen: TPhoneScreen;
    pinScreen: TPinScreen;
  };
  redirectTo: string;
}

const SignIn = (props: ISignInProps) => (
  <SignInProvider>
    <SignInTemplate {...props} />
  </SignInProvider>
);

const getServerSidePropsHandler: GetServerSideProps = async ({
  query,
  res,
}) => {
  const removeAuthTokenCookie = removeServerCookie(AUTH_TOKEN);
  const removeAuthUserDataCookie = removeServerCookie(AUTH_USER);

  res.setHeader('Set-Cookie', [
    removeAuthTokenCookie,
    removeAuthUserDataCookie,
  ]);

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  );

  const content = localizedContentService<ISignInProps>('pt-BR', ['signIn']);

  let redirectTo = '/';

  if (query && query.redirect) {
    const noHasScript = !query.redirect.includes('<script>');

    if (noHasScript) {
      redirectTo = query.redirect as string;
    }
  }

  return {
    props: {
      ...content,
      redirectTo,
    },
  };
};

export const getServerSideProps = withEnhancedPageHandler(
  getServerSidePropsHandler
);

export default SignIn;
